// src/pages/ManageTaskPage.jsx
import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { connect } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import {
  Inbox,
  Check,
  X,
  User as UserIcon,
  Clock,
  Calendar,
  MessageCircle,
  Edit,
} from "lucide-react";
import { request } from "../services/networking/request";
import { API_CONSTANT_MAP } from "../utils/constants/endpoints";
import NavBar from "../components/layout/NavBar";
import OuterContainer from "../components/layout/OuterContainer";
import BackButton from "../components/BackButton";
import VerificationBadge from "../components/common/VerificationBadge";
import { format } from "date-fns";
import { parseDate } from "../utils/helpers/date";

/* ============== THEME ============== */
const theme = {
  colors: {
    primary: "#3b82f6",
    primaryLight: "#eff6ff",
    primaryText: "#3b82f6",
    textHeading: "#0f172a",
    textBody: "#334155",
    textMuted: "#64748b",
    background: "#f8fafc",
    surface: "#ffffff",
    border: "#e2e8f0",
    success: "#059669",
    ok: "#16a34a",
    danger: "#dc2626",
    purple: "#7c3aed",
    blueDeep: "#2563eb", // Updated to match brand colors
  },
  shadows: {
    sm: "0 1px 3px rgba(15, 23, 42, 0.06)",
    md: "0 4px 16px rgba(15, 23, 42, 0.08)",
    lg: "0 8px 32px rgba(15, 23, 42, 0.12)",
  },
  font: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
  borderRadius: "16px",
};

/* ============== PAGE ============== */
const Page = styled.div`
  min-height: 100vh;
  background: linear-gradient(180deg, #fafbff 0%, #f8fafc 50%, #f1f5f9 100%);
`;

const Container = styled.div`
  max-width: 1280px;
  margin: 0 auto;
  padding: 24px;
  @media (max-width: 1024px) {
    padding: 0;
    width: 100%;
    max-width: 100vw;
    overflow-x: hidden;
  }
`;

const Header = styled.header`
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  align-items: flex-start;
  margin-bottom: 1.5rem;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 1rem;
    align-items: stretch;
  }
`;

const HeaderLeft = styled.div`
  display: grid;
  grid-auto-flow: column;
  align-items: center;
  gap: 12px;
  min-width: 0;

  @media (max-width: 768px) {
    grid-auto-flow: row;
    gap: 8px;
    justify-items: start;
  }
`;

const TitleWrap = styled.div`
  min-width: 0;
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
`;

const EditIcon = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background: none;
  border: none;
  border-radius: 10px;
  color: ${theme.colors.textHeading};
  cursor: pointer;
  transition: all 0.2s ease;
  flex-shrink: 0;
  margin-top: 2px;

  &:hover {
    background: linear-gradient(135deg, #3b82f6 0%, #6366f1 100%);
    color: white;
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(59, 130, 246, 0.2);
  }
`;

const Title = styled.h1`
  margin: 0;
  color: ${theme.colors.textHeading};
  font-size: 1.55rem;
  font-weight: 800;
  letter-spacing: 0.2px;
  overflow-wrap: anywhere;

  @media (max-width: 768px) {
    font-size: 1.25rem;
    line-height: 1.3;
  }
`;

const SubTitle = styled.div`
  margin-top: 4px;
  color: ${theme.colors.textMuted};
  font-size: 0.95rem;
`;

/* ——— Mobile: 2x2 grid of action buttons ——— */
const ButtonBar = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    display: grid;
    grid-template-columns: 1fr 1fr; /* 2 by 2 */
    gap: 8px;

    & > button {
      width: 100%;
      min-width: 0;
      font-size: 0.9rem;
      padding: 10px 10px;
      justify-content: center;
    }
  }
`;

/* ============== Buttons (flatter) ============== */
const Btn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 9px 12px;
  border-radius: 10px;
  cursor: pointer;
  font-weight: 800;
  font-size: 0.95rem;
  transition: transform 100ms ease, background 120ms ease, color 120ms ease;
`;

const PrimaryBtn = styled(Btn)`
  background: linear-gradient(135deg, #3b82f6 0%, #6366f1 100%);
  color: #ffffff;
  border: none;
  box-shadow: 0 4px 14px rgba(59, 130, 246, 0.25);

  &:hover {
    background: linear-gradient(135deg, #2563eb 0%, #5b5bf6 100%);
    color: #fff;
    box-shadow: 0 6px 20px rgba(59, 130, 246, 0.35);
    transform: translateY(-2px);
  }
`;

const QuietBtn = styled(Btn)`
  background: linear-gradient(135deg, #ffffff 0%, #fafbff 100%);
  color: ${theme.colors.textBody};
  border: 1px solid #f1f5f9;
  box-shadow: ${theme.shadows.sm};

  &:hover {
    background: linear-gradient(
      135deg,
      ${theme.colors.primaryLight} 0%,
      #f0f7ff 100%
    );
    color: ${theme.colors.primary};
    transform: translateY(-1px);
    box-shadow: ${theme.shadows.md};
    border-color: ${theme.colors.primary};
  }
`;

const SuccessBtn = styled(PrimaryBtn)`
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  box-shadow: 0 4px 14px rgba(16, 185, 129, 0.25);

  &:hover {
    background: linear-gradient(135deg, #059669 0%, #047857 100%);
    box-shadow: 0 6px 20px rgba(16, 185, 129, 0.35);
    transform: translateY(-2px);
  }
`;

const DangerBtn = styled(Btn)`
  background: linear-gradient(135deg, #ffffff 0%, #fafbff 100%);
  color: #dc2626;
  border: 1px solid #fecaca;
  box-shadow: ${theme.shadows.sm};

  &:hover {
    background: linear-gradient(135deg, #fef2f2 0%, #fff1f1 100%);
    color: #b91c1c;
    border-color: #fca5a5;
    box-shadow: ${theme.shadows.md};
    transform: translateY(-1px);
  }
`;

/* ============== Card (clean, airy) ============== */
const Card = styled.section`
  background: ${theme.colors.surface};
  border-radius: ${theme.borderRadius};
  box-shadow: ${theme.shadows.md};
  padding: 1.5rem;
  margin-bottom: 1.5rem;
  border: 1px solid ${theme.colors.border};

  @media (max-width: 768px) {
    padding: 1rem;
    margin-bottom: 1.5rem;
  }
`;

/* thin separators (no heavy boxes) */
const Divider = styled.hr`
  border: 0;
  height: 1px;
  background: ${theme.colors.border};
  opacity: 0.6;
  margin: 1rem 0;
`;

/* ============== Status / Stats ============== */
const StatusRow = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
  align-items: center;
  gap: 12px;
`;

const StatusTitle = styled.h2`
  margin: 0;
  color: ${theme.colors.textHeading};
  font-size: 1.02rem;
  font-weight: 800;
`;

const badgeColors = {
  pending: { bg: "hsl(35, 91%, 95%)", fg: "hsl(35, 91%, 45%)" },
  accepted: { bg: "hsl(145, 63%, 95%)", fg: "hsl(145, 63%, 35%)" },
  open: { bg: "hsl(145, 63%, 95%)", fg: "hsl(145, 63%, 35%)" },
  rejected: { bg: "hsl(0, 84%, 95%)", fg: "hsl(0, 84%, 45%)" },
  withdrawn: { bg: "hsl(0, 84%, 95%)", fg: "hsl(0, 84%, 45%)" },
  in_progress: { bg: "#f0f7ff", fg: "#3b82f6" },
  completed: { bg: "hsl(145, 63%, 95%)", fg: "hsl(145, 63%, 35%)" },
  cancelled: { bg: "hsl(0, 84%, 95%)", fg: "hsl(0, 84%, 45%)" },
};

const StatusBadge = styled.span`
  background: ${({ status }) => badgeColors[status]?.bg || "#f0f7ff"};
  color: ${({ status }) => badgeColors[status]?.fg || theme.colors.primary};
  padding: 0.4rem 0.75rem;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 700;
  text-transform: capitalize;
  border: 1px solid rgba(0, 0, 0, 0.05);
  letter-spacing: -0.01em;
  box-shadow: ${theme.shadows.sm};
`;

const StatsRow = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;
  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const Stat = styled.div`
  padding: 10px 0;
`;

const StatLabel = styled.div`
  color: ${theme.colors.textMuted};
  font-weight: 700;
  font-size: 0.86rem;
`;

const StatValue = styled.div`
  color: ${theme.colors.textHeading};
  font-weight: 900;
  margin-top: 2px;
`;

/* ============== Tabs (simple pills) ============== */
const Tabs = styled.div`
  margin-bottom: 1.5rem;

  @media (max-width: 768px) {
    margin-bottom: 1.5rem;
  }
`;
const TabBar = styled.div`
  display: flex;
  gap: 2px;
  background: #f8fafc;
  border-radius: 12px;
  padding: 4px;
  box-shadow: inset 0 1px 3px rgba(15, 23, 42, 0.1);
  width: fit-content;
  max-width: 100%;
  overflow-x: auto;

  @media (max-width: 768px) {
    width: 100%;
    gap: 2px;
    padding: 4px;
  }
`;

const TabBtn = styled.button`
  flex: 1;
  background: ${(props) =>
    props.active
      ? "linear-gradient(135deg, #3b82f6 0%, #6366f1 100%)"
      : "transparent"};
  border: none;
  border-radius: 10px;
  padding: 8px 12px;
  font-weight: 700;
  font-size: 0.9rem;
  color: ${(props) => (props.active ? "#ffffff" : theme.colors.textMuted)};
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: ${(props) =>
    props.active ? "0 2px 8px rgba(59, 130, 246, 0.2)" : "none"};
  transform: ${(props) => (props.active ? "translateY(-1px)" : "none")};
  white-space: nowrap;
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;

  &:hover {
    color: ${(props) => (props.active ? "#ffffff" : "#3b82f6")};
    background: ${(props) =>
      props.active
        ? "linear-gradient(135deg, #2563eb 0%, #5b5bf6 100%)"
        : "rgba(255, 255, 255, 0.5)"};
  }

  @media (max-width: 768px) {
    padding: 10px 8px;
    font-size: 0.85rem;

    svg {
      display: none;
    }
  }
`;

/* ============== Offer item (separate cards, more spacing) ============== */
const OfferCard = styled.div`
  background: ${theme.colors.surface};
  border: 1px solid ${theme.colors.border};
  border-radius: 12px;
  padding: 1rem;
  box-shadow: ${theme.shadows.sm};
  margin-bottom: 1rem;
  transition: all 0.2s ease;

  &:hover {
    box-shadow: ${theme.shadows.md};
    border-color: ${theme.colors.primary};
    transform: translateY(-1px);
  }

  @media (max-width: 768px) {
    padding: 0.875rem;
    margin-bottom: 0.875rem;
    border-radius: 10px;
  }
`;

const OfferContent = styled.article`
  display: flex;
  flex-direction: column;
  gap: 0;
`;

const OfferHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem;
  margin-bottom: 0.875rem;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 0.5rem;
    align-items: stretch;
  }
`;

const UserBlock = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
  flex: 1;
  min-width: 0;

  @media (max-width: 768px) {
    gap: 10px;
  }
`;

const Avatar = styled.div`
  width: 42px;
  height: 42px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(
    135deg,
    ${theme.colors.primaryLight} 0%,
    #f0f7ff 100%
  );
  color: ${theme.colors.primary};
  overflow: hidden;
  border: 2px solid rgba(59, 130, 246, 0.1);
  flex-shrink: 0;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  @media (max-width: 768px) {
    width: 36px;
    height: 36px;
  }
`;

const UserInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  flex: 1;
  min-width: 0;
`;

const UserName = styled.div`
  font-weight: 600;
  color: ${theme.colors.textHeading};
  cursor: pointer;
  text-decoration: none;
  transition: color 0.15s ease;
  font-size: 1rem;
  letter-spacing: -0.01em;
  line-height: 1.3;

  &:hover {
    color: ${theme.colors.primary};
  }

  @media (max-width: 768px) {
    font-size: 0.95rem;
  }
`;

const UserMeta = styled.div`
  font-size: 0.8125rem;
  color: ${theme.colors.textMuted};
  font-weight: 500;
  line-height: 1.4;
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.75rem;
  margin: 0.75rem 0;

  @media (max-width: 768px) {
    grid-template-columns: repeat(3, 1fr);
    gap: 0.5rem;
  }
`;

const StatItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  padding: 0.5rem 0.375rem;
  transition: all 0.2s ease;
  position: relative;
  overflow: hidden;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
    border-color: ${theme.colors.primary};
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 2px;
    background: linear-gradient(90deg, ${theme.colors.primary}, #6366f1);
    opacity: 0;
    transition: opacity 0.2s ease;
  }

  &:hover::before {
    opacity: 1;
  }

  @media (max-width: 768px) {
    padding: 0.4375rem 0.3125rem;
    border-radius: 4px;
  }
`;

const OfferStatValue = styled.div`
  font-weight: 600;
  color: ${theme.colors.textHeading};
  display: flex;
  gap: 0.1875rem;
  align-items: center;
  justify-content: center;
  font-size: 0.8125rem;
  letter-spacing: -0.01em;
  margin-bottom: 0.1875rem;
  line-height: 1.1;

  @media (max-width: 768px) {
    font-size: 0.75rem;
  }
`;

const OfferStatLabel = styled.div`
  font-size: 0.5625rem;
  color: ${theme.colors.textMuted};
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  line-height: 1.1;
  opacity: 0.75;
`;

const ExperienceTag = styled.span`
  display: inline-block;
  background: ${theme.colors.primaryLight};
  color: ${theme.colors.primary};
  font-weight: 700;
  font-size: 0.75rem;
  padding: 0.25rem 0.5rem;
  border-radius: 12px;
  border: 1px solid rgba(0, 0, 0, 0.05);
  letter-spacing: -0.01em;
  box-shadow: ${theme.shadows.sm};
  white-space: nowrap;
`;

const SectionTitle = styled.h4`
  margin: 0.875rem 0 0.4rem;
  color: ${theme.colors.textHeading};
  font-weight: 600;
  font-size: 0.875rem;
  letter-spacing: -0.01em;
  line-height: 1.3;

  &:first-of-type {
    margin-top: 0.75rem;
  }
`;

const SectionContent = styled.div`
  margin-bottom: 0.875rem;

  p {
    margin: 0;
    color: ${theme.colors.textBody};
    line-height: 1.5;
    font-size: 0.875rem;
  }
`;

const SectionDescription = styled.div`
  background: ${theme.colors.primaryLight};
  border: 1px solid #dbeafe;
  border-radius: 10px;
  padding: 0.875rem 1rem;
  margin-bottom: 1.25rem;
  font-size: 0.8125rem;
  color: ${theme.colors.textBody};
  line-height: 1.5;

  strong {
    color: ${theme.colors.textHeading};
    font-weight: 600;
  }
`;

const Skeleton = styled.div`
  border-radius: 12px;
  background: linear-gradient(90deg, #f1f5f9, #f8fafc, #f1f5f9);
  background-size: 200% 100%;
  animation: shimmer 1.2s infinite linear;
  height: ${(p) => p.h || "14px"};
  width: ${(p) => p.w || "100%"};

  @keyframes shimmer {
    from {
      background-position: 200% 0;
    }
    to {
      background-position: -200% 0;
    }
  }
`;

const ActionBar = styled.div`
  display: flex;
  gap: 0.75rem;
  justify-content: flex-end;
  align-items: center;
  margin-top: 0.875rem;
  padding-top: 0.75rem;
  border-top: 1px solid ${theme.colors.border};

  @media (max-width: 768px) {
    justify-content: stretch;
    gap: 0.5rem;
    padding-top: 0.625rem;
    margin-top: 0.75rem;

    button {
      flex: 1;
      font-size: 0.85rem;
      padding: 0.7rem 0.8rem;
      justify-content: center;
      min-width: 0;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }
`;

const ReplyForm = styled.div`
  margin-top: 16px;
  padding: 16px;
  background: #f9fafb;
  border-radius: 12px;
  border: 1px solid ${theme.colors.border};
`;

const ReplyTextarea = styled.textarea`
  width: 100%;
  min-height: 80px;
  padding: 12px;
  border: 1px solid ${theme.colors.border};
  border-radius: 8px;
  font-family: inherit;
  font-size: 0.95rem;
  resize: vertical;
  margin-bottom: 12px;

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary};
    box-shadow: 0 0 0 3px ${theme.colors.primaryLight};
  }

  &::placeholder {
    color: ${theme.colors.textMuted};
  }
`;

const ReplyActions = styled.div`
  display: flex;
  gap: 8px;
  justify-content: flex-end;
`;

const ReplyButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.12s ease;

  &.primary {
    background: linear-gradient(135deg, #3b82f6 0%, #6366f1 100%);
    color: white;
    border: 1px solid #3b82f6;

    &:hover {
      background: linear-gradient(135deg, #2563eb 0%, #5b5bf6 100%);
    }
  }

  &.secondary {
    background: white;
    color: ${theme.colors.textBody};
    border: 1px solid ${theme.colors.border};

    &:hover {
      background: ${theme.colors.primaryLight};
      color: ${theme.colors.primary};
    }
  }
`;

/* ============== Modal Styles ============== */
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 20px;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 16px;
  padding: 24px;
  width: 100%;
  max-width: 500px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  animation: modalSlideIn 0.2s ease-out;
  
  @keyframes modalSlideIn {
    from {
      opacity: 0;
      transform: translateY(-20px) scale(0.95);
    }
    to {
      opacity: 1;
      transform: translateY(0) scale(1);
    }
  }

  @media (max-width: 768px) {
    margin: 20px;
    padding: 20px;
  }
`;

const ModalTitle = styled.h3`
  margin: 0 0 16px;
  color: ${theme.colors.textHeading};
  font-size: 1.25rem;
  font-weight: 700;
`;

const ModalDescription = styled.p`
  margin: 0 0 20px;
  color: ${theme.colors.textBody};
  font-size: 0.95rem;
  line-height: 1.5;
`;

const ModalTextarea = styled.textarea`
  width: 100%;
  min-height: 100px;
  padding: 12px;
  border: 1px solid ${theme.colors.border};
  border-radius: 8px;
  font-family: inherit;
  font-size: 0.95rem;
  resize: vertical;
  margin-bottom: 20px;

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary};
    box-shadow: 0 0 0 3px ${theme.colors.primaryLight};
  }

  &::placeholder {
    color: ${theme.colors.textMuted};
  }
`;

const ModalActions = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;

  @media (max-width: 768px) {
    flex-direction: column;
    
    button {
      width: 100%;
      justify-content: center;
    }
  }
`;

const ModalButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.95rem;
  cursor: pointer;
  transition: all 0.12s ease;

  &.primary {
    background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
    color: white;
    border: 1px solid #ef4444;

    &:hover {
      background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%);
    }
  }

  &.secondary {
    background: white;
    color: ${theme.colors.textBody};
    border: 1px solid ${theme.colors.border};

    &:hover {
      background: ${theme.colors.primaryLight};
      color: ${theme.colors.primary};
    }
  }
`;

/* ============== Component ============== */
const ManageTaskPage = ({ state }) => {
  const { taskId } = useParams();
  const navigate = useNavigate();
  const [task, setTask] = useState(null);
  const [offers, setOffers] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [activeTab, setActiveTab] = useState("pending");
  const [loading, setLoading] = useState(true);
  const [replyingTo, setReplyingTo] = useState(null);
  const [replyText, setReplyText] = useState("");
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [rejectingOfferId, setRejectingOfferId] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [cancelModalOpen, setCancelModalOpen] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const resp = await request("POST", API_CONSTANT_MAP.fetch_task_by_id, {
          task_id: taskId,
        });
        setTask(resp.data.task);
        setOffers(resp.data.task.offers || []);

        // Fetch questions for this task
        const questionsResp = await request(
          "POST",
          API_CONSTANT_MAP.fetch_task_questions,
          {
            task_id: taskId,
          }
        );
        setQuestions(questionsResp.data?.questions || []);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    })();
  }, [taskId]);

  if (loading || !task) {
    return (
      <>
        <NavBar user={state.user} />
        <OuterContainer>
          <Page>
            <Container>
              {/* Header Skeleton */}
              <Header>
                <HeaderLeft>
                  <Skeleton h="20px" w="20px" />
                  <TitleWrap>
                    <Skeleton
                      h="32px"
                      w="300px"
                      style={{ marginBottom: "8px" }}
                    />
                    <Skeleton h="16px" w="400px" />
                  </TitleWrap>
                </HeaderLeft>
                <ButtonBar>
                  <Skeleton
                    h="40px"
                    w="120px"
                    style={{ borderRadius: "10px" }}
                  />
                  <Skeleton
                    h="40px"
                    w="100px"
                    style={{ borderRadius: "10px" }}
                  />
                  <Skeleton
                    h="40px"
                    w="90px"
                    style={{ borderRadius: "10px" }}
                  />
                </ButtonBar>
              </Header>

              {/* Status Card Skeleton */}
              <Card>
                <StatusRow>
                  <Skeleton h="24px" w="120px" />
                  <Skeleton
                    h="28px"
                    w="80px"
                    style={{ borderRadius: "999px" }}
                  />
                </StatusRow>
                <Divider />
                <StatsRow>
                  <Stat>
                    <Skeleton
                      h="16px"
                      w="60px"
                      style={{ marginBottom: "4px" }}
                    />
                    <Skeleton h="20px" w="80px" />
                  </Stat>
                  <Stat>
                    <Skeleton
                      h="16px"
                      w="80px"
                      style={{ marginBottom: "4px" }}
                    />
                    <Skeleton h="20px" w="40px" />
                  </Stat>
                  <Stat>
                    <Skeleton
                      h="16px"
                      w="60px"
                      style={{ marginBottom: "4px" }}
                    />
                    <Skeleton h="20px" w="100px" />
                  </Stat>
                </StatsRow>
              </Card>

              {/* Tabs Skeleton */}
              <Tabs>
                <TabBar>
                  <Skeleton
                    h="36px"
                    w="120px"
                    style={{ borderRadius: "999px" }}
                  />
                  <Skeleton
                    h="36px"
                    w="130px"
                    style={{ borderRadius: "999px" }}
                  />
                  <Skeleton
                    h="36px"
                    w="120px"
                    style={{ borderRadius: "999px" }}
                  />
                </TabBar>
              </Tabs>

              {/* Offers Skeleton */}
              {[1, 2, 3].map((i) => (
                <OfferCard key={i}>
                  <OfferContent>
                    {/* Offer Header */}
                    <OfferHeader>
                      <UserBlock>
                        <Skeleton
                          h="48px"
                          w="48px"
                          style={{ borderRadius: "50%" }}
                        />
                        <UserInfo>
                          <Skeleton
                            h="18px"
                            w="150px"
                            style={{ marginBottom: "4px" }}
                          />
                          <Skeleton h="14px" w="120px" />
                        </UserInfo>
                      </UserBlock>
                      <Skeleton
                        h="28px"
                        w="80px"
                        style={{ borderRadius: "20px" }}
                      />
                    </OfferHeader>

                    {/* Stats Grid */}
                    <StatsGrid>
                      <StatItem>
                        <Skeleton
                          h="20px"
                          w="80px"
                          style={{ marginBottom: "4px" }}
                        />
                        <Skeleton h="12px" w="100px" />
                      </StatItem>
                      <StatItem>
                        <Skeleton
                          h="20px"
                          w="60px"
                          style={{ marginBottom: "4px" }}
                        />
                        <Skeleton h="12px" w="80px" />
                      </StatItem>
                      <StatItem>
                        <Skeleton
                          h="20px"
                          w="70px"
                          style={{ marginBottom: "4px" }}
                        />
                        <Skeleton h="12px" w="90px" />
                      </StatItem>
                    </StatsGrid>

                    {/* Experience Tag */}
                    <Skeleton
                      h="32px"
                      w="150px"
                      style={{ borderRadius: "999px", margin: "4px 0 10px" }}
                    />

                    {/* Proposal Section */}
                    <SectionTitle style={{ marginBottom: "6px" }}>
                      <Skeleton h="18px" w="80px" />
                    </SectionTitle>
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "8px",
                        marginBottom: "12px",
                      }}
                    >
                      <Skeleton h="16px" w="100%" />
                      <Skeleton h="16px" w="95%" />
                      <Skeleton h="16px" w="80%" />
                    </div>

                    {/* Action Buttons */}
                    <ActionBar>
                      <Skeleton
                        h="36px"
                        w="80px"
                        style={{ borderRadius: "10px" }}
                      />
                      <Skeleton
                        h="36px"
                        w="120px"
                        style={{ borderRadius: "10px" }}
                      />
                    </ActionBar>
                  </OfferContent>
                </OfferCard>
              ))}
            </Container>
          </Page>
        </OuterContainer>
      </>
    );
  }

  const pending = offers.filter((o) => o.status === "pending");
  const accepted = offers.filter((o) => o.status === "accepted");
  const declined = offers.filter((o) =>
    ["rejected", "withdrawn"].includes(o.status)
  );
  const current = { pending, accepted, declined, questions }[activeTab];

  const updateOfferStatus = async (offerId, newStatus, rejectReason = null) => {
    try {
      const payload = { offer_id: offerId, status: newStatus };
      if (newStatus === "rejected" && rejectReason) {
        payload.reject_reason = rejectReason;
      }
      
      const resp = await request(
        "POST",
        API_CONSTANT_MAP.update_task_offer_status,
        payload
      );
      if (!resp.data.success)
        throw new Error(resp.data.error?.message || "Unknown error");

      const conversationId = resp?.data?.conversation_id || null;
      if (resp.data.task_status) {
        setTask((t) => ({ ...t, status: resp.data.task_status }));
      }

      setOffers((prev) =>
        prev.map((o) => {
          if (o.id === offerId) {
            return {
              ...o,
              status: newStatus,
              auto_rejected: false,
              conversation_id:
                newStatus === "accepted"
                  ? conversationId || o.conversation_id || null
                  : o.conversation_id || null,
            };
          }
          if (newStatus === "accepted" && o.status === "pending") {
            return { ...o, status: "rejected", auto_rejected: true };
          }
          return o;
        })
      );
    } catch (err) {
      console.error(`Failed to ${newStatus} offer:`, err);
      alert(`❌ Could not ${newStatus} offer: ${err.message}`);
    }
  };

  const handleAccept = (offerId) => updateOfferStatus(offerId, "accepted");
  
  const handleDecline = (offerId) => {
    setRejectingOfferId(offerId);
    setRejectModalOpen(true);
  };

  const handleRejectSubmit = async () => {
    if (!rejectingOfferId) return;
    
    try {
      await updateOfferStatus(rejectingOfferId, "rejected", rejectReason.trim() || null);
      
      // Close modal and reset state
      setRejectModalOpen(false);
      setRejectingOfferId(null);
      setRejectReason("");
    } catch (err) {
      console.error("Failed to reject offer:", err);
      // The error handling is already done in updateOfferStatus
    }
  };

  const handleRejectCancel = () => {
    setRejectModalOpen(false);
    setRejectingOfferId(null);
    setRejectReason("");
  };

  const handleCancelTask = () => {
    setCancelModalOpen(true);
  };

  const handleCancelTaskConfirm = async () => {
    try {
      const resp = await request("POST", API_CONSTANT_MAP.cancel_task, {
        task_id: task.id,
      });
      
      if (!resp.data.success) {
        throw new Error(resp.data.error?.message || "Failed to cancel task");
      }

      // Update task status to cancelled
      setTask((t) => ({ ...t, status: "cancelled", cancelled_at: new Date().toISOString() }));
      
      // Close modal
      setCancelModalOpen(false);
      
      alert("✅ Task has been cancelled successfully");
    } catch (err) {
      console.error("Failed to cancel task:", err);
      alert(`❌ Could not cancel task: ${err.message}`);
    }
  };

  const handleCancelTaskCancel = () => {
    setCancelModalOpen(false);
  };

  const handleMarkComplete = async () => {
    try {
      const resp = await request("POST", API_CONSTANT_MAP.update_task_status, {
        task_id: task.id,
        status: "completed",
      });
      if (!resp.data.success)
        throw new Error(resp.data.error?.message || "Unknown error");
      setTask({ ...task, status: "completed" });
    } catch (err) {
      console.error("Failed to mark complete:", err);
      alert(`❌ Could not complete task: ${err.message}`);
    }
  };

  const handleCancelCollaboration = async (acceptedOfferId) => {
    try {
      const resp = await request(
        "POST",
        API_CONSTANT_MAP.update_task_offer_status,
        { offer_id: acceptedOfferId, status: "withdrawn" }
      );
      if (!resp.data.success) throw new Error(resp.data.error?.message);
      setTask((t) => ({ ...t, status: resp.data.task_status }));
      setOffers((list) =>
        list.map((o) => {
          if (o.id === acceptedOfferId) return { ...o, status: "withdrawn" };
          if (o.auto_rejected)
            return { ...o, status: "pending", auto_rejected: false };
          return o;
        })
      );
    } catch (err) {
      console.error("Cancel collaboration failed:", err);
      alert(`❌ ${err.message}`);
    }
  };

  const acceptedWithChat = accepted.find((o) => !!o.conversation_id);

  const handleReplySubmit = async (questionId) => {
    if (!replyText.trim()) return;

    try {
      const response = await request(
        "POST",
        API_CONSTANT_MAP.post_task_question_reply,
        {
          question_id: questionId,
          reply_text: replyText.trim(),
        }
      );

      if (response.data?.success) {
        // Refresh questions to show the new reply
        const questionsResp = await request(
          "POST",
          API_CONSTANT_MAP.fetch_task_questions,
          {
            task_id: taskId,
          }
        );
        setQuestions(questionsResp.data?.questions || []);

        // Reset reply form
        setReplyingTo(null);
        setReplyText("");
      } else {
        alert("Failed to submit reply. Please try again.");
      }
    } catch (error) {
      console.error("Error submitting reply:", error);
      alert("Error submitting reply. Please try again.");
    }
  };

  return (
    <>
      <NavBar user={state.user} />
      <OuterContainer>
        <Page>
          <Container>
            <BackButton onClick={() => navigate(-1)} size={16} title="Back" />
            <Header>
              <HeaderLeft>
                <TitleWrap>
                  <div>
                    <Title
                      style={{ textTransform: "capitalize", cursor: "pointer" }}
                      onClick={() => navigate(`/tasks/${taskId}`)}
                      title="Click to view task page"
                    >
                      {task.title}
                    </Title>
                    <SubTitle>
                      Manage offers and communication for your task
                    </SubTitle>
                  </div>
                  <EditIcon
                    onClick={() => navigate(`/edit/task/${taskId}`)}
                    title="Edit Task"
                  >
                    <Edit size={16} />
                  </EditIcon>
                </TitleWrap>
              </HeaderLeft>

              <ButtonBar>
                {acceptedWithChat?.conversation_id && (
                  <PrimaryBtn
                    onClick={() =>
                      navigate(`/chat/${acceptedWithChat.conversation_id}`)
                    }
                    title="Open project chat"
                  >
                    <MessageCircle size={16} /> Project Chat
                  </PrimaryBtn>
                )}
                {task.status === "in_progress" && (
                  <SuccessBtn onClick={handleMarkComplete}>
                    <Check size={16} /> Mark as Complete
                  </SuccessBtn>
                )}
                {(task.status === "open" || task.status === "in_progress") && (
                  <DangerBtn onClick={handleCancelTask} title="Cancel this task">
                    <X size={16} /> Cancel Task
                  </DangerBtn>
                )}
              </ButtonBar>
            </Header>

            <Card>
              <StatusRow>
                <StatusTitle>Task Status</StatusTitle>
                <StatusBadge status={task.status}>
                  {task.status
                    .split("_")
                    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
                    .join(" ")}
                </StatusBadge>
              </StatusRow>

              <Divider />

              <StatsRow>
                <Stat>
                  <StatLabel>Budget</StatLabel>
                  <StatValue>${Number(task.budget).toFixed(2)}</StatValue>
                </Stat>
                <Stat>
                  <StatLabel>Total Offers</StatLabel>
                  <StatValue>{offers.length}</StatValue>
                </Stat>
                <Stat>
                  <StatLabel>Created</StatLabel>
                  <StatValue>
                    {format(parseDate(task.created_at), "MMM d, yyyy")}
                  </StatValue>
                </Stat>
              </StatsRow>
            </Card>

            <Tabs>
              <TabBar>
                <TabBtn
                  active={activeTab === "pending"}
                  onClick={() => setActiveTab("pending")}
                >
                  <Inbox size={16} /> Pending ({pending.length})
                </TabBtn>
                <TabBtn
                  active={activeTab === "accepted"}
                  onClick={() => setActiveTab("accepted")}
                >
                  <Check size={16} /> Accepted ({accepted.length})
                </TabBtn>
                <TabBtn
                  active={activeTab === "declined"}
                  onClick={() => setActiveTab("declined")}
                >
                  <X size={16} /> Declined ({declined.length})
                </TabBtn>
                <TabBtn
                  active={activeTab === "questions"}
                  onClick={() => setActiveTab("questions")}
                >
                  <MessageCircle size={16} /> Questions ({questions.length})
                </TabBtn>
              </TabBar>
            </Tabs>

            {/* Section Descriptions */}
            {activeTab === "pending" && (
              <SectionDescription>
                <strong>Pending Offers:</strong> {task.status === "cancelled" 
                  ? "This task has been cancelled. All pending offers have been automatically declined." 
                  : "These are new offers submitted by freelancers that are waiting for your review. You can accept or decline each offer based on the proposal, budget, and timeline."
                }
              </SectionDescription>
            )}

            {activeTab === "accepted" && (
              <SectionDescription>
                <strong>Accepted Offers:</strong> These are offers you've
                approved. Once accepted, a project chat is created where you can
                collaborate with the freelancer. You can cancel the
                collaboration if needed.
              </SectionDescription>
            )}

            {activeTab === "declined" && (
              <SectionDescription>
                <strong>Declined Offers:</strong> These are offers you've
                rejected. This is for your reference to track what offers you've
                previously reviewed and declined.
              </SectionDescription>
            )}

            {activeTab === "questions" && (
              <SectionDescription>
                <strong>Questions:</strong> Freelancers can ask questions about
                your task before submitting offers. Answer questions to help
                clarify requirements and attract better proposals.
              </SectionDescription>
            )}

            {activeTab === "questions" ? (
              questions.length > 0 ? (
                questions.map((q) => (
                  <OfferCard key={q.id}>
                    <OfferContent>
                      <OfferHeader>
                        <UserBlock>
                          <Avatar>
                            {q.avatar ? (
                              <img src={q.avatar} alt="User avatar" />
                            ) : (
                              <UserIcon size={18} />
                            )}
                          </Avatar>
                          <div>
                            <UserName style={{ textTransform: "capitalize" }}>
                              {q.user_name || "Anonymous User"}
                            </UserName>
                            <div
                              style={{
                                fontSize: ".85rem",
                                color: theme.colors.textMuted,
                              }}
                            >
                              Asked a question
                            </div>
                          </div>
                        </UserBlock>
                        <div
                          style={{
                            fontSize: ".85rem",
                            color: theme.colors.textMuted,
                          }}
                        >
                          {parseDate(q.created_at).toLocaleDateString()}
                        </div>
                      </OfferHeader>
                      <div
                        style={{
                          margin: "12px 0",
                          fontSize: ".95rem",
                          color: theme.colors.textBody,
                        }}
                      >
                        {q.question_text || q.text}
                      </div>

                      {/* Display replies if they exist */}
                      {q.replies && q.replies.length > 0 && (
                        <div
                          style={{
                            marginTop: "16px",
                            paddingLeft: "16px",
                            borderLeft: `2px solid ${theme.colors.border}`,
                          }}
                        >
                          {q.replies.map((reply) => (
                            <div
                              key={reply.id}
                              style={{
                                marginBottom: "12px",
                                padding: "8px 0",
                              }}
                            >
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "8px",
                                  marginBottom: "6px",
                                }}
                              >
                                <Avatar style={{ width: 24, height: 24 }}>
                                  {reply.avatar ? (
                                    <img
                                      src={reply.avatar}
                                      alt="Reply user avatar"
                                    />
                                  ) : (
                                    <UserIcon size={12} />
                                  )}
                                </Avatar>
                                <div
                                  style={{
                                    fontWeight: 600,
                                    fontSize: "0.85rem",
                                  }}
                                >
                                  {reply.user_name || "Anonymous User"}
                                </div>
                                <div
                                  style={{
                                    fontSize: "0.75rem",
                                    color: theme.colors.textMuted,
                                  }}
                                >
                                  {parseDate(
                                    reply.created_at
                                  ).toLocaleDateString()}
                                </div>
                              </div>
                              <div
                                style={{
                                  fontSize: "0.9rem",
                                  color: theme.colors.textBody,
                                  paddingLeft: "32px",
                                }}
                              >
                                {reply.reply_text || reply.text}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Reply form for task poster */}
                      {replyingTo === q.id ? (
                        <ReplyForm>
                          <ReplyTextarea
                            value={replyText}
                            onChange={(e) => setReplyText(e.target.value)}
                            placeholder="Write your reply..."
                          />
                          <ReplyActions>
                            <ReplyButton
                              className="secondary"
                              onClick={() => {
                                setReplyingTo(null);
                                setReplyText("");
                              }}
                            >
                              Cancel
                            </ReplyButton>
                            <ReplyButton
                              className="primary"
                              onClick={() => handleReplySubmit(q.id)}
                              disabled={!replyText.trim()}
                            >
                              Submit Reply
                            </ReplyButton>
                          </ReplyActions>
                        </ReplyForm>
                      ) : (
                        <div style={{ marginTop: "12px" }}>
                          <ReplyButton
                            className="secondary"
                            onClick={() => setReplyingTo(q.id)}
                          >
                            <MessageCircle size={14} /> Reply to Question
                          </ReplyButton>
                        </div>
                      )}
                    </OfferContent>
                  </OfferCard>
                ))
              ) : (
                <Card style={{ textAlign: "center", padding: "48px 24px" }}>
                  <SectionTitle>No Questions Yet</SectionTitle>
                  <p style={{ color: theme.colors.textMuted }}>
                    No one has asked questions about this task yet.
                  </p>
                </Card>
              )
            ) : current.length > 0 ? (
              current.map((o) => (
                <OfferCard key={o.id}>
                  <OfferContent>
                    <OfferHeader>
                      <UserBlock>
                        <Avatar>
                          {o.user.avatar ? (
                            <img src={o.user.avatar} alt="User avatar" />
                          ) : (
                            <UserIcon size={18} />
                          )}
                        </Avatar>
                        <UserInfo>
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "0.5rem",
                              flexWrap: "wrap",
                            }}
                          >
                            <UserName
                              style={{ textTransform: "capitalize" }}
                              onClick={() => {
                                window.location.href = `/users/${o.user.id}`;
                              }}
                            >
                              {o.user.username ||
                                `${o.user.first_name} ${o.user.last_name}`}
                              <VerificationBadge 
                                isVerified={o.user.email_verified} 
                                size="14px" 
                                spacing="4px"
                              />
                            </UserName>
                            {o.years_experience && (
                              <ExperienceTag>
                                {o.years_experience} years experience
                              </ExperienceTag>
                            )}
                          </div>
                          <UserMeta>
                            Submitted{" "}
                            {format(parseDate(o.created_at), "MMM d, yyyy")}
                          </UserMeta>
                        </UserInfo>
                      </UserBlock>
                      <StatusBadge status={o.status}>{o.status}</StatusBadge>
                    </OfferHeader>

                    <StatsGrid>
                      <StatItem>
                        <OfferStatValue>
                          <UserIcon size={16} /> ${o.offer_amount}
                        </OfferStatValue>
                        <OfferStatLabel>Proposed Budget</OfferStatLabel>
                      </StatItem>
                      <StatItem>
                        <OfferStatValue>
                          <Clock size={16} /> {o.duration || "—"}
                        </OfferStatValue>
                        <OfferStatLabel>Duration</OfferStatLabel>
                      </StatItem>
                      <StatItem>
                        <OfferStatValue>
                          <Calendar size={16} />{" "}
                          {o.completion_date
                            ? format(parseDate(o.completion_date), "MMM d")
                            : "—"}
                        </OfferStatValue>
                        <OfferStatLabel>Completion</OfferStatLabel>
                      </StatItem>
                    </StatsGrid>

                    <SectionTitle>Proposal</SectionTitle>
                    <SectionContent>
                      <p>{o.proposal}</p>
                    </SectionContent>

                    {o.previous_similar_work && (
                      <>
                        <SectionTitle>Previous Similar Work</SectionTitle>
                        <SectionContent>
                          <p>{o.previous_similar_work}</p>
                        </SectionContent>
                      </>
                    )}

                    {activeTab === "pending" && task.status !== "cancelled" ? (
                      <ActionBar>
                        <QuietBtn onClick={() => handleDecline(o.id)}>
                          <X size={16} /> Decline
                        </QuietBtn>
                        <PrimaryBtn onClick={() => handleAccept(o.id)}>
                          <Check size={16} /> Accept Offer
                        </PrimaryBtn>
                      </ActionBar>
                    ) : activeTab === "accepted" ? (
                      <ActionBar>
                        {task.status !== "completed" && task.status !== "cancelled" && (
                          <DangerBtn
                            onClick={() => handleCancelCollaboration(o.id)}
                          >
                            <X size={16} /> Cancel Collaboration
                          </DangerBtn>
                        )}
                        {o.conversation_id && task.status !== "cancelled" ? (
                          <PrimaryBtn
                            onClick={() =>
                              navigate(`/chat/${o.conversation_id}`)
                            }
                          >
                            <MessageCircle size={16} /> Go to Project Chat
                          </PrimaryBtn>
                        ) : null}
                      </ActionBar>
                    ) : null}
                  </OfferContent>
                </OfferCard>
              ))
            ) : (
              <Card style={{ textAlign: "center", padding: "30px 18px" }}>
                <div
                  style={{
                    fontWeight: 800,
                    marginBottom: 4,
                    color: theme.colors.textHeading,
                  }}
                >
                  No {activeTab} offers
                </div>
                <div style={{ color: theme.colors.textMuted }}>
                  {activeTab === "pending"
                    ? "New offers will appear here when freelancers submit them."
                    : activeTab === "accepted"
                    ? "Accepted offers will appear here."
                    : "Declined offers will appear here."}
                </div>
              </Card>
            )}
          </Container>
        </Page>
      </OuterContainer>

      {/* Reject Reason Modal */}
      {rejectModalOpen && (
        <ModalOverlay onClick={handleRejectCancel}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalTitle>Decline Offer</ModalTitle>
            <ModalDescription>
              Would you like to provide feedback to help the freelancer improve their future proposals? This is optional but can be valuable for their growth.
            </ModalDescription>
            <ModalTextarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder=""
              maxLength={500}
            />
            <ModalActions>
              <ModalButton className="secondary" onClick={handleRejectCancel}>
                Cancel
              </ModalButton>
              <ModalButton className="primary" onClick={handleRejectSubmit}>
                <X size={16} /> Decline Offer
              </ModalButton>
            </ModalActions>
          </ModalContent>
        </ModalOverlay>
      )}

      {/* Cancel Task Modal */}
      {cancelModalOpen && (
        <ModalOverlay onClick={handleCancelTaskCancel}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalTitle>Cancel Task</ModalTitle>
            <ModalDescription>
              Are you sure you want to cancel this task? This action cannot be undone. All pending offers will be automatically declined and any ongoing collaborations will be ended.
            </ModalDescription>
            <ModalActions>
              <ModalButton className="secondary" onClick={handleCancelTaskCancel}>
                Keep Task
              </ModalButton>
              <ModalButton className="primary" onClick={handleCancelTaskConfirm}>
                <X size={16} /> Cancel Task
              </ModalButton>
            </ModalActions>
          </ModalContent>
        </ModalOverlay>
      )}
    </>
  );
};

export default connect((state) => ({ state }))(ManageTaskPage);
